import { useState } from "react";
import { useDbClient } from "@tanstack/react-db";
import type { WikiPagePublic } from "@softmax/observatory-client/types";
import type { WikiIdentity } from "../wiki.collections";
import { deleteWikiPage as deleteWikiPageRecord } from "../wiki.mutations";
import type { SetMode, SetError } from "./wiki-editor.types";

export function useWikiDeletion({
  wikiSlug,
  pageSlug,
  elevatedPrivileges,
  page,
  accessLost,
  setMode,
  setError,
}: WikiIdentity & {
  page: WikiPagePublic | null;
  accessLost: boolean;
  setMode: SetMode;
  setError: SetError;
}) {
  const db = useDbClient();
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [lostAccess, setLostAccess] = useState(accessLost);

  if (accessLost !== lostAccess) {
    setLostAccess(accessLost);

    if (accessLost) setDeleteReason("");
  }

  function deletePage() {
    if (!page) return;

    setDeleting(true);
    setError(null);
    deleteWikiPageRecord({
      db,
      wikiSlug,
      pageSlug,
      elevatedPrivileges,
      pageId: page.id,
      reason: deleteReason,
    })
      .then(() => {
        setConfirmingDelete(false);
        setMode("read");
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Delete failed"),
      )
      .finally(() => setDeleting(false));
  }

  return {
    deleting,
    confirmingDelete,
    setConfirmingDelete,
    deleteReason,
    setDeleteReason,
    deletePage,
  };
}
